import { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { Modal, Button, Spinner } from "react-bootstrap";
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionActive from './SubscriptionActive';
import SubscriptionInactive from './SubscriptionInactive';
import { SPECIFIED_COMMERCIAL_TRANSACTION_LAW_PATH } from "../../constants/Constant";


loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const API_URL = process.env.REACT_APP_STRIPE_CHECKOUT_API_URL;

const SubscriptionModal = (props) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showSCTL, setShowSCTL] = useState(false); // 特定商取引法を表示中か

  useEffect(() => {
    const fetchUserAttributes = async () => {
      setIsInitialLoading(true);
      try {
        // キャッシュを無視して最新の属性を取得
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const attributes = await Auth.userAttributes(user);
        const status = attributes.find(attr => attr.Name === "custom:subscription_status")?.Value || "none";
        setSubscriptionStatus(status);
      } catch (e) {
        console.error("Error fetching subscription status:", e);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (props.show) {
      fetchUserAttributes();
      setShowSCTL(false); // モーダルを開くときは常に初期画面
    }
  }, [props.show]);

  // Stripe Checkout へ全画面遷移した後にブラウザの「戻る」で返ってくると、
  // bfcache（戻る/進むキャッシュ）が JS 状態ごと復元するため loading=true のまま
  // ボタンのスピナーが回り続ける。bfcache 復元（pageshow.persisted）と、非 bfcache の
  // 再表示（visibilitychange）の両方でリセットする。
  useEffect(() => {
    const reset = () => setLoading(false);
    const onPageShow = (e) => {
      if (e.persisted) reset();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reset();
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Stripe Checkout セッション作成
  const handleCheckout = async () => {
    setLoading(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // 戻り先URL（パラメータは PaymentSuccessOverlay が検知するためここで付与）
      const success_url = `${window.location.origin}${window.location.pathname}?session_id={CHECKOUT_SESSION_ID}${window.location.hash}`;
      const cancel_url = `${window.location.origin}${window.location.pathname}?cancel=true${window.location.hash}`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ success_url: success_url, cancel_url: cancel_url }),
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url; // 成功時は loading=false にせずそのまま遷移
      } else {
        throw new Error("決済URLの取得に失敗しました");
      }
    } catch (e) {
      console.error(e);
      alert("決済エラーが発生しました");
      setLoading(false);
    }
  };

  return (
    <Modal {...props} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          {showSCTL ? "特定商取引法に基づく表記" : "サブスクリプションのご案内"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ display: 'flex', flexDirection: 'column', padding: showSCTL ? '0' : '1rem' }}>
        {isInitialLoading ? (
          <div className="d-flex justify-content-center align-items-center flex-grow-1">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : showSCTL ? (
          /* --- 特定商取引法ビュー --- */
          <div style={{ flexGrow: 1 }}>
            <iframe
              src={SPECIFIED_COMMERCIAL_TRANSACTION_LAW_PATH}
              title="特定商取引法に基づく表記"
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: '600px' }}
            />
          </div>
        ) : (
          <div style={{ flexGrow: 1 }}>
            {subscriptionStatus === "active" ? (
              /* --- 管理画面 --- */
              <SubscriptionActive />
            ) : (
              /* --- 案内（未契約）画面 --- */
              <SubscriptionInactive onCheckout={handleCheckout} loading={loading} />
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center">
        {/* 左側：切り替えリンク */}
        <div>
          {showSCTL ? (
            <Button variant="link" size="sm" onClick={() => setShowSCTL(false)}>
              ← 元に戻る
            </Button>
          ) : (
            <Button variant="link" size="sm" onClick={() => setShowSCTL(true)} style={{ fontSize: '0.8rem' }}>
              特定商取引法に基づく表記
            </Button>
          )}
        </div>

        {/* 右側：閉じるボタン */}
        <Button variant="link" onClick={props.onHide}>
          閉じる
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionModal;